import type { TenantAccess } from '@konyaxl/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiRequestError, api } from '../core/api.js';
import { gorseliKucult } from '../core/gorselKucult.js';
import { keepScreenAwake, onHardwareScan, vibrate } from '../core/native.js';
import { startScannerListener } from '../core/scanner.js';
import { sesEslestirmeTamam, sesHata, unlockAudio } from '../core/sounds.js';
import { CameraScanner, cameraSupported } from './CameraScanner.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
}

interface Satir {
  id: string;
  kod: string;
  aciklama: string;
  gorselVar: boolean;
  durum: 'gonderiliyor' | 'kayitli' | 'hata';
  not?: string;
}

/**
 * Barkodsuz ürün kaydı.
 *
 * Eşleştirme yok: barkodu olmayan parça için elde ne varsa o alınıyor —
 * ürün kodu, fotoğraf, açıklama. Üçü de isteğe bağlı ama en az biri şart,
 * yoksa kayıt hiçbir şeyi tanımlamaz. Ürün kodu girilmediğinde parçayı
 * ayırt edecek tek şey fotoğraf kaldığı için o zorunlu oluyor.
 */
export function BarkodsuzPage({ tenant, onBack }: Props) {
  const [kod, setKod] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [gorsel, setGorsel] = useState<{ dataUri: string; bayt: number } | null>(null);
  const [satirlar, setSatirlar] = useState<Satir[]>([]);
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [kameraAcik, setKameraAcik] = useState(false);
  /** Bu ürün kodu kategoride kaç kez kayıtlı — 0 ise uyarı çıkmaz. */
  const [tekrar, setTekrar] = useState(0);
  const dosyaRef = useRef<HTMLInputElement>(null);

  /** Görsel zorunluluğu: ürün kodu yoksa fotoğraf olmadan kaydedilemez. */
  const gorselZorunlu = kod.trim().length === 0;
  const kaydedilebilir = gorsel !== null || kod.trim().length > 0;

  useEffect(() => {
    let birak: (() => void) | undefined;
    void keepScreenAwake().then((f) => (birak = f));
    return () => birak?.();
  }, []);

  const koduAl = useCallback((okunan: string) => {
    const temiz = okunan.trim();
    if (!temiz) return;
    unlockAudio();
    setKod(temiz);
    setKameraAcik(false);
    vibrate(30);
  }, []);

  /** El terminali okuyucusu ve USB okuyucular. */
  useEffect(() => {
    const wedgeDurdur = startScannerListener({ onScan: koduAl });
    const dwDurdur = onHardwareScan(koduAl);
    return () => {
      wedgeDurdur();
      dwDurdur();
    };
  }, [koduAl]);

  /**
   * Tekrar kontrolü. Engelleyici değil: aynı ürün kodundan birden çok parça
   * olabiliyor, karar operatörün. Sorgu gecikmeli çünkü kod elle yazılırken
   * her harfte istek atmanın anlamı yok; okutulduğunda kod tek seferde
   * geldiği için bekleme hissedilmiyor.
   */
  useEffect(() => {
    const temiz = kod.trim();
    if (!temiz) {
      setTekrar(0);
      return;
    }

    let iptal = false;
    const zaman = setTimeout(() => {
      void api
        .get<{ count: number }>(
          `/${tenant.slug}/entry-check?code=${encodeURIComponent(temiz)}`,
        )
        .then((res) => {
          if (!iptal) setTekrar(res.count);
        })
        .catch(() => {
          // Uyarı gösterilemese de kayıt engellenmez.
        });
    }, 350);

    return () => {
      iptal = true;
      clearTimeout(zaman);
    };
  }, [kod, tenant.slug]);

  async function dosyaSecildi(dosya: Blob) {
    setHata(null);
    try {
      const kucuk = await gorseliKucult(dosya);
      setGorsel({ dataUri: kucuk.dataUri, bayt: kucuk.bayt });
    } catch {
      setHata('Fotoğraf işlenemedi');
    }
  }

  async function kaydet() {
    if (!kaydedilebilir || mesgul) return;

    setMesgul(true);
    setHata(null);

    const id = crypto.randomUUID();
    const satir: Satir = {
      id,
      kod: kod.trim(),
      aciklama: aciklama.trim(),
      gorselVar: gorsel !== null,
      durum: 'gonderiliyor',
    };
    setSatirlar((eski) => [satir, ...eski.slice(0, 49)]);

    try {
      await api.post(`/${tenant.slug}/entries`, {
        clientUuid: id,
        code: kod.trim() || undefined,
        aciklama: aciklama.trim() || undefined,
        image: gorsel?.dataUri,
      });

      setSatirlar((eski) =>
        eski.map((s) => (s.id === id ? { ...s, durum: 'kayitli' } : s)),
      );
      sesEslestirmeTamam();
      vibrate(50);

      // Sıradaki ürün için temizlenir; peş peşe kayıt yapılıyor.
      setKod('');
      setAciklama('');
      setGorsel(null);
      setTekrar(0);
    } catch (err) {
      const mesaj =
        err instanceof ApiRequestError ? err.message : 'Kayıt gönderilemedi';
      setSatirlar((eski) =>
        eski.map((s) => (s.id === id ? { ...s, durum: 'hata', not: mesaj } : s)),
      );
      setHata(mesaj);
      sesHata();
      vibrate(220);
    } finally {
      setMesgul(false);
    }
  }

  const kayitliSayisi = satirlar.filter((s) => s.durum === 'kayitli').length;

  return (
    <div className="tarama">
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name}</h1>
        <div className="ust-bar-sag">
          <span>{kayitliSayisi} kayıt</span>
        </div>
      </header>

      {kameraAcik && (
        <CameraScanner onScan={koduAl} onClose={() => setKameraAcik(false)} />
      )}

      <div className="barkodsuz-form">
        <label className="alan">
          <span className="alan-etiket">{tenant.scanRules.a.label}</span>
          <div className="alan-satir">
            <input
              data-manual-input="true"
              value={kod}
              onChange={(e) => setKod(e.target.value)}
              placeholder="Okutun ya da elle girin"
              spellCheck={false}
            />
            {cameraSupported() && (
              <button
                className={`btn-kamera ${kameraAcik ? 'acik' : ''}`}
                onClick={() => setKameraAcik((a) => !a)}
              >
                {kameraAcik ? 'Kapat' : 'Kamera'}
              </button>
            )}
          </div>
          {tekrar > 0 && (
            <div className="tekrar-uyari">
              <strong>Ürün tekrarı var.</strong> Bu ürün kodu bu kategoride daha
              önce {tekrar} kez kaydedilmiş. Yine de kaydedebilirsiniz.
            </div>
          )}
        </label>

        <label className="alan">
          <span className="alan-etiket">
            Fotoğraf
            {gorselZorunlu && <em className="zorunlu-not">ürün kodu yoksa zorunlu</em>}
          </span>

          {gorsel ? (
            <div className="gorsel-onizleme">
              <img src={gorsel.dataUri} alt="Seçilen fotoğraf" />
              <div className="gorsel-arac">
                <span>{Math.round(gorsel.bayt / 1024)} KB</span>
                <button className="btn-iptal dar" onClick={() => setGorsel(null)}>
                  Kaldır
                </button>
              </div>
            </div>
          ) : (
            <button
              className={`gorsel-sec ${gorselZorunlu ? 'zorunlu' : ''}`}
              onClick={() => dosyaRef.current?.click()}
            >
              📷 Fotoğraf çek veya seç
            </button>
          )}

          <input
            ref={dosyaRef}
            type="file"
            accept="image/*"
            /* Telefonda doğrudan kamerayı açar; masaüstünde dosya seçtirir. */
            capture="environment"
            hidden
            onChange={(e) => {
              const dosya = e.target.files?.[0];
              if (dosya) void dosyaSecildi(dosya);
              e.target.value = '';
            }}
          />
        </label>

        <label className="alan">
          <span className="alan-etiket">
            Açıklama <em className="zorunlu-not">isteğe bağlı</em>
          </span>
          <textarea
            data-manual-input="true"
            value={aciklama}
            onChange={(e) => setAciklama(e.target.value)}
            placeholder="Ürün hakkında not"
            rows={2}
            maxLength={500}
          />
        </label>

        {hata && <div className="hata-kutu">{hata}</div>}

        <button
          className="btn-birincil"
          onClick={() => void kaydet()}
          disabled={!kaydedilebilir || mesgul}
        >
          {mesgul
            ? 'Kaydediliyor…'
            : kaydedilebilir
              ? 'Kaydet'
              : 'Ürün kodu ya da fotoğraf gerekli'}
        </button>
      </div>

      <ul className="satir-liste">
        {satirlar.map((s) => (
          <li key={s.id} className={`satir ${s.durum === 'hata' ? 'hata' : 'kayitli'}`}>
            <span className="satir-kodlar">
              {s.kod || <em className="kodsuz">kodsuz</em>}
              {s.gorselVar && <span className="rozet-foto">foto</span>}
              {s.aciklama && <span className="satir-not">{s.aciklama}</span>}
            </span>
            <span className="satir-durum">
              {s.durum === 'gonderiliyor' && '⋯'}
              {s.durum === 'kayitli' && '✓'}
              {s.durum === 'hata' && `✕ ${s.not ?? ''}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
